import { errorFrom } from "@/lib/errors";
import { useTranslation } from "react-i18next";
import { HeroCharacter } from "@/components/brand/HeroCharacter";
import { useEffect, useState } from "react";
import { useAccessGate } from "@/lib/access";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Clapperboard, Plus, Loader2, Trash2, ArrowUpRight, Film, Clock, Link2 } from "lucide-react";
import { Layout } from "@/components/hud/Layout";
import { Button } from "@/components/primitives/Button";
import { Input } from "@/components/primitives/Input";
import {
  listMontages,
  createMontage,
  deleteMontage,
  fmtDuration,
  type MontageSummary,
} from "@/lib/montageApi";

export default function Montage() {
  const { t } = useTranslation();
  const [items, setItems] = useState<MontageSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const { guard, isAuthenticated } = useAccessGate();

  const load = () =>
    listMontages()
      .then(setItems)
      .catch(() => toast.error(t("montage.loadFailed")))
      .finally(() => setLoading(false));

  useEffect(() => {
    if (isAuthenticated) load();
    else { setItems([]); setLoading(false); } // anonymous exploration: no private data requested
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    if (!(await guard())) return; // login required BEFORE creating a project (FREE tool, no subscription)
    setSaving(true);
    try {
      await createMontage(title.trim());
      setTitle("");
      toast.success(t("montage.created"));
      load();
    } catch {
      toast.error(t("montage.createFailed"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm(t("montage.deleteConfirm"))) return;
    try {
      await deleteMontage(id);
      setItems((p) => p.filter((m) => m.id !== id));
    } catch (err: any) {
      toast.error(errorFrom(err, "montage.deleteFailed"));
    }
  };

  return (
    <Layout>
        <main data-testid="montage-page" className="relative mx-auto max-w-6xl px-6 pb-32 pt-16 sm:px-10 lg:pt-20">
          <header className="relative flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <HeroCharacter variant="montage" className="-right-44 -top-12" />
            <div className="relative">
              <span className="eyebrow">{t("montage.eyebrow")}</span>
              <h1 className="headline-shadow mt-5 font-display text-4xl font-bold uppercase leading-[0.98] tracking-wide text-foreground sm:text-5xl lg:text-6xl">
                <span className="text-neon">{t("nav.montage")}</span>
              </h1>
              <p className="mt-4 max-w-xl text-sm text-[#B8C4DA] sm:text-base">
                {t("montage.subtitle")}
              </p>
            </div>
          </header>

          <div className="glass brackets relative mt-10 rounded-2xl p-6 sm:p-8">
            <span className="bk" />
            <div aria-hidden className="pointer-events-none absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-[#7FD4FF]/70 to-transparent" />
            <form onSubmit={create} className="flex flex-col gap-4 sm:flex-row sm:items-end">
              <div className="flex-1">
                <Input
                  label={t("montage.newTitleLabel")}
                  data-testid="montage-title-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t("montage.newTitlePlaceholder")}
                  maxLength={120}
                />
              </div>
              <Button data-testid="montage-create-btn" type="submit" size="lg" className="h-11" disabled={saving || !title.trim()}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                {t("montage.new")}
              </Button>
            </form>
          </div>

          {loading ? (
            <div className="flex justify-center py-16 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div
              data-testid="montage-empty"
              className="glass relative mt-10 overflow-hidden rounded-3xl px-8 py-20 text-center"
            >
              <div aria-hidden className="absolute inset-0 bg-[radial-gradient(60%_60%_at_50%_100%,rgba(46,123,255,0.2),transparent_65%)]" />
              <div className="relative">
                <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl border border-[#2E7BFF]/50 bg-[#05070D] shadow-[0_0_36px_-8px_rgba(46,123,255,0.8)]">
                  <Clapperboard className="h-7 w-7 text-[#7FD4FF]" strokeWidth={1.5} />
                </div>
                <p className="mt-6 font-display text-2xl font-bold uppercase tracking-wide text-foreground">{t("montage.emptyTitle")}</p>
                <p className="mt-2 text-sm text-muted-foreground">{t("montage.emptyBody")}</p>
              </div>
            </div>
          ) : (
            <ul data-testid="montage-list" className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((m, i) => (
                <li key={m.id} className="animate-in fade-in slide-in-from-bottom-3 fill-mode-both duration-500" style={{ animationDelay: `${Math.min(i, 8) * 60}ms` }}>
                  <div
                    data-testid={`montage-card-${m.id}`}
                    className="group relative overflow-hidden rounded-2xl border border-[#2E7BFF]/20 bg-[#0C1220] transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1.5 hover:border-[#2E7BFF]/70 hover:shadow-[0_30px_70px_-30px_rgba(0,0,0,0.95),0_0_50px_-14px_rgba(46,123,255,0.65)]"
                  >
                    <div className="relative h-28 overflow-hidden border-b border-[#2E7BFF]/15 bg-[#060A14]">
                      <div aria-hidden className="absolute inset-0 bg-[radial-gradient(70%_70%_at_50%_120%,rgba(46,123,255,0.3),transparent_60%),linear-gradient(180deg,#0B1428,#070C18)] transition-transform duration-700 group-hover:scale-105" />
                      <div aria-hidden className="absolute inset-0 opacity-[0.10] [background-image:linear-gradient(rgba(46,123,255,0.9)_1px,transparent_1px),linear-gradient(90deg,rgba(46,123,255,0.9)_1px,transparent_1px)] [background-size:22px_22px] [mask-image:radial-gradient(80%_80%_at_50%_50%,black,transparent)]" />
                      {/* filmstrip */}
                      <div className="absolute inset-x-5 top-1/2 flex -translate-y-1/2 gap-1.5">
                        {Array.from({ length: 6 }).map((_, k) => (
                          <span key={k} className="h-10 flex-1 rounded-sm border border-[#2E7BFF]/40 bg-[#05070D]/70" style={{ opacity: 1 - k * 0.12 }} />
                        ))}
                      </div>
                      <span className="absolute left-3 top-3 rounded-md border border-[#2E7BFF]/40 bg-[#05070D]/85 px-2 py-0.5 font-display text-[11px] font-bold tracking-[0.16em] text-[#7FD4FF]">
                        {m.aspect_ratio ?? "9:16"}
                      </span>
                    </div>
                    <div className="p-5">
                      <p className="line-clamp-1 font-display text-lg font-bold uppercase tracking-wide text-foreground">{m.title}</p>
                      <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1.5"><Film className="h-3.5 w-3.5 text-[#7FD4FF]" strokeWidth={1.75} />{t("montage.videoCount", { count: m.item_count })}</span>
                        <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-[#7FD4FF]" strokeWidth={1.75} />{fmtDuration(m.total_duration)}</span>
                      </div>
                      <div className="mt-5 flex items-center justify-between">
                        <button
                          data-testid={`montage-delete-${m.id}`}
                          onClick={() => remove(m.id)}
                          title={t("common.delete")}
                          className="icon-action icon-action-danger"
                        >
                          <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                        </button>
                        <Link to={`/montage/${m.id}`} data-testid={`montage-open-${m.id}`}>
                          <Button size="sm" variant="secondary">
                            {t("common.open")} <ArrowUpRight className="h-3.5 w-3.5" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* Convert: secondary utility, discreet entry point (bottom-right). Existing route, no logic change. */}
          <div className="mt-16 flex justify-end">
            <Link
              to="/simulation-studios"
              data-testid="montage-convert-link"
              className="glass inline-flex items-center gap-3 rounded-xl px-4 py-2.5 !shadow-none transition-[border-color,transform,box-shadow] hover:-translate-y-0.5 hover:border-[#2E7BFF]/60 hover:shadow-[0_0_24px_-10px_rgba(46,123,255,0.8)]"
            >
              <span className="grid h-8 w-8 place-items-center rounded-lg border border-[#2E7BFF]/40 bg-[#05070D]/80 text-[#7FD4FF]">
                <Link2 className="h-4 w-4" strokeWidth={1.75} />
              </span>
              <span className="text-left">
                <span className="block font-display text-xs font-bold uppercase tracking-[0.18em] text-foreground">{t("nav.convert")}</span>
                <span className="block text-[11px] text-muted-foreground">{t("montage.convertHint")}</span>
              </span>
              <ArrowUpRight className="h-3.5 w-3.5 text-[#7FD4FF]" />
            </Link>
          </div>
        </main>
    </Layout>
  );
}
